<div class="container-fluid px-5 py-3">
    <div class="d-flex pb-2">
        <span class="text-blue font-weight-bold">
            <h3 translate>icashComparison.report</h3>
        </span>
    </div>
    <div class="my-2 py-2">
        <!-- ******************************************************
        ************** Search Card Form *************
        ****************************************************** -->
        <form [formGroup]="searchForm">
            <div class="input-group mb-2">
                <!-- store -->
                <div>
                    <span class="input-group-text" translate>icashComparison.store</span>
                </div>
                <select class="custom-select" aria-describedby="storeId" formControlName="storeId" style="max-width: 220pt;" (change)="onStoreChange()">
                    <!-- No empty option: the report compares one store against one technical
                         point, so there is nothing sensible to show without a store picked.
                         A КТП employee sees only their own store; for everyone else the stores
                         with no technical point linked are listed but marked - hiding them would
                         make most of the estate vanish with no explanation. -->
                    <option *ngFor="let s of stores" [value]=s.storeId>
                        {{s.name}}<span *ngIf="isUnmatchedStore(s.storeId)"> ({{'icashComparison.noTechnicalPoint' | translate}})</span>
                    </option>
                </select>

                <!-- date from -->
                <div class="ml-2">
                    <span class="input-group-text" id="dateFrom" translate>technicalInspection.startDate</span>
                </div>
                <input class="form-control d-inline" aria-describedby="dateFrom" style="max-width: 130pt;"
                       formControlName="dateFrom" ngbDatepicker #d1="ngbDatepicker" readonly>
                <button class="btn btn-outline-secondary fa fa-calendar" (click)="d1.toggle()" type="button" style="height: 38px; border-radius: 0;"></button>

                <!-- date to -->
                <div class="ml-2">
                    <span class="input-group-text" id="dateTo" translate>technicalInspection.endDate</span>
                </div>
                <input class="form-control d-inline" aria-describedby="dateTo" style="max-width: 130pt;"
                       formControlName="dateTo" ngbDatepicker #d2="ngbDatepicker" readonly>
                <button class="btn btn-outline-secondary fa fa-calendar" (click)="d2.toggle()" type="button" style="height: 38px; border-radius: 0;"></button>

                <!-- status -->
                <div class="input-group-prepend ml-2">
                    <span class="input-group-text" translate>icashComparison.statusLabel</span>
                </div>
                <select class="custom-select" aria-describedby="status" formControlName="status" style="max-width: 200pt;">
                    <option value="ALL" translate>icashComparison.filter.all</option>
                    <option value="MATCHED" translate>icashComparison.filter.matched</option>
                    <option value="DIFFERENT" translate>icashComparison.filter.different</option>
                    <option value="MISSING_RECEIPT" translate>icashComparison.filter.missingReceipt</option>
                </select>

                <!-- type -->
                <div class="input-group-prepend ml-2">
                    <span class="input-group-text" translate>icashComparison.type</span>
                </div>
                <select class="custom-select" aria-describedby="subjectType" formControlName="subjectType" style="max-width: 170pt;">
                    <!-- Blank is the default and shows both kinds, which is the point of the
                         screen: a receipt belongs to one or the other. -->
                    <option value="" translate>placeholder.select</option>
                    <option value="TECHNICAL_INSPECTION" translate>icashComparison.type.TECHNICAL_INSPECTION</option>
                    <option value="SALE" translate>icashComparison.type.SALE</option>
                </select>

                <!-- actions -->
                <div class="input-group-append ml-2 action-buttons">
                    <button class="btn btn-outline-success" type="button" (click)="search()" translate>button.search</button>
                    <button class="btn btn-outline-danger ml-2" type="button" (click)="clearSearch()" translate>button.clear</button>
                </div>
            </div>
        </form>

        <!-- The chosen store has no counterpart on our side, so there is nothing to compare its
             receipts against. Said plainly, rather than showing every receipt as a missing
             inspection - which would look like a serious problem and mean nothing. -->
        <div class="alert alert-warning my-3" *ngIf="selectedStoreUnmatched">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            <span translate>icashComparison.storeNotLinked</span>
        </div>

        <!-- A КТП employee whose point carries no iCash обект ID: the dropdown is empty, so
             without this the screen would just sit there blank with nothing to explain it. -->
        <div class="alert alert-warning my-3" *ngIf="noStoreAvailable">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            <span translate>icashComparison.noStoreForUser</span>
        </div>

        <!-- ******************************************************
        ************** Summary *************
        ****************************************************** -->
        <div class="card my-3" [hidden]="!dataLoaded || nothingToReport">
            <div class="card-body">
                <h6 class="card-title text-muted" translate>icashComparison.summary</h6>
                <div class="row">
                    <!-- Each tile carries the overall figure, then the same figure split between
                         inspections and sales - a problem sitting in one of them would otherwise
                         be averaged into a total that looks healthy. -->
                    <div class="col">
                        <div class="border rounded p-3 h-100">
                            <div class="h4 mb-0">{{totalInspections + totalSales}}</div>
                            <small class="text-muted" translate>icashComparison.summary.total</small>
                            <div class="small text-muted mt-1">
                                <span translate>icashComparison.summary.inspections</span> {{totalInspections}}
                                &middot;
                                <span translate>icashComparison.summary.sales</span> {{totalSales}}
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="border rounded p-3 h-100">
                            <div class="h4 mb-0 text-success">{{matchedCount}}</div>
                            <small class="text-muted" translate>icashComparison.summary.matched</small>
                            <div class="small text-muted mt-1">
                                <span translate>icashComparison.summary.inspections</span> {{inspectionMatchedCount}}
                                &middot;
                                <span translate>icashComparison.summary.sales</span> {{saleMatchedCount}}
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="border rounded p-3 h-100">
                            <div class="h4 mb-0 text-warning">{{differentCount}}</div>
                            <small class="text-muted" translate>icashComparison.summary.different</small>
                            <div class="small text-muted mt-1">
                                <span translate>icashComparison.summary.inspections</span> {{inspectionDifferentCount}}
                                &middot;
                                <span translate>icashComparison.summary.sales</span> {{saleDifferentCount}}
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="border rounded p-3 h-100">
                            <div class="h4 mb-0 text-danger">{{missingReceiptCount}}</div>
                            <small class="text-muted" translate>icashComparison.summary.missingReceipt</small>
                            <div class="small text-muted mt-1">
                                <span translate>icashComparison.summary.inspections</span> {{inspectionMissingReceiptCount}}
                                &middot;
                                <span translate>icashComparison.summary.sales</span> {{saleMissingReceiptCount}}
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="border rounded p-3 h-100">
                            <div class="h4 mb-0">{{totalDifference | number:'1.2-2'}} {{currencyCode}}</div>
                            <small class="text-muted" translate>icashComparison.summary.totalDifference</small>
                        </div>
                    </div>
                </div>
                <div class="small text-muted mt-3">
                    <span class="badge badge-success mr-1">&nbsp;</span>
                    <span class="mr-3" translate>icashComparison.legend.matched</span>
                    <span class="badge badge-warning mr-1">&nbsp;</span>
                    <span class="mr-3" translate>icashComparison.legend.different</span>
                    <span class="badge badge-danger mr-1">&nbsp;</span>
                    <span translate>icashComparison.legend.missingReceipt</span>
                </div>
            </div>
        </div>

        <!-- ******************************************************
        ************** Table Data *************
        ****************************************************** -->
        <!-- table-responsive belongs on the wrapper, not the table: Bootstrap gives it
             display:block, which makes a table shrink to its content and ignore w-100. -->
        <div class="table-responsive">
            <table class="table table-bordered table-striped w-100" [hidden]="!dataLoaded || nothingToReport">
                <thead>
                    <tr>
                        <th style="width: 9%;" scope="col" translate>icashComparison.type</th>
                        <th style="width: 14%;" scope="col" translate>icashComparison.inspectionCompletedAt</th>
                        <th style="width: 11%;" scope="col" translate>icashComparison.registrationNumber</th>
                        <th style="width: 11%;" scope="col" translate>icashComparison.inspectionTotal</th>
                        <th style="width: 11%;" scope="col" translate>icashComparison.receiptCompletedAt</th>
                        <th style="width: 11%;" scope="col" translate>icashComparison.receiptTotal</th>
                        <th style="width: 9%;" scope="col" translate>icashComparison.difference</th>
                        <th style="width: 8%;" scope="col" translate>icashComparison.statusLabel</th>
                        <th style="width: 9%;" scope="col" translate>icashComparison.pairing</th>
                        <th style="width: 7%;" scope="col" translate>col.actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let item of content">
                        <td class="text-nowrap">
                            <span class="badge"
                                  [ngClass]="item.subjectType === 'SALE' ? 'badge-info' : 'badge-secondary'"
                                  translate>{{subjectTypeKey(item.subjectType)}}</span>
                        </td>
                        <td>{{item.inspectionCompletedAt}}</td>
                        <!-- A sale need not have one: a walk-in buying goods has no vehicle. -->
                        <td>{{item.registrationNumber || '—'}}</td>
                        <td class="text-right text-nowrap">
                            {{item.inspectionTotal != null ? (item.inspectionTotal | number:'1.2-2') : '—'}}
                        </td>
                        <td>{{item.receiptCompletedAt || '—'}}</td>
                        <td class="text-right text-nowrap">
                            {{item.receiptTotal != null ? (item.receiptTotal | number:'1.2-2') : '—'}}
                        </td>
                        <td class="text-right text-nowrap">
                            {{item.difference != null ? (item.difference | number:'1.2-2') : '—'}}
                        </td>
                        <td>
                            <span [ngClass]="matchStatusClass(item.matchStatus)" translate>{{matchStatusKey(item.matchStatus)}}</span>
                        </td>
                        <td class="text-nowrap">
                            <span *ngIf="item.pairingMethod" translate>{{pairingMethodKey(item.pairingMethod)}}</span>
                            <span *ngIf="!item.pairingMethod">—</span>
                            <!-- A number was typed but nothing in iCash carries it: the row still
                                 pairs on time, so without this marker a typo would read as a clean
                                 match. -->
                            <i class="fa fa-exclamation-triangle text-warning ml-1"
                               *ngIf="item.fiscalNumberUnmatched"
                               data-toggle="tooltip" data-placement="bottom"
                               title="{{'icashComparison.pairing.fiscalNumberUnmatched' | translate}}"></i>
                        </td>
                        <td class="text-center py-2">
                            <!-- Disabled without a receipt: there is no operationId to fetch, so
                                 the call would fail and the popup open empty. -->
                            <button class="btn btn-sm btn-primary" type="button"
                                [disabled]="!item.receiptOperationId"
                                (click)="viewComparison(item)"
                                data-toggle="tooltip" data-placement="bottom" title="{{'button.view' | translate}}">
                                <i class="fa fa-search fa-lg"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <ng-container *ngIf="dataLoaded; else dataLoadingTemplate"></ng-container>
        <!-- ******************************************************
        ************** Pagination *************
        ****************************************************** -->
        <div class="paging-wrapper d-flex justify-content-between action-pagination">
            <select class="pagination-size custom-select" [(ngModel)]="sortingPaging.pageSize" [ngModelOptions]="{standalone: true}" (change)="onPageSizeChange($event)">
                <option *ngFor="let item of pageSizeOptions; let i = index" [value]="pageSizeOptions[i]">
                    {{pageSizeOptions[i]}} {{'paging.itemsPerPage' | translate}}
                </option>
            </select>
            <ngb-pagination class="pagination-control" [(page)]="sortingPaging.pageNumber"
                [pageSize]="sortingPaging.pageSize" [collectionSize]="sortingPaging.totalElements"
                [boundaryLinks]="true" [maxSize]="maxSize" (pageChange)="pageChanged($event)">
            </ngb-pagination>
            <span class="pagination-index border center">
                {{sortingPaging.fromRow}} - {{sortingPaging.toRow}} / {{sortingPaging.totalElements}}
            </span>
        </div>
    </div>
</div>

<ng-template #dataLoadingTemplate>
    <div *ngIf="errorMessage$ | async as errorMessage; else dataSpinner">
        <div class="alert alert-danger">
            {{errorMessage}}
        </div>
        <div class="d-flex justify-content-center">
            <em class="fa fa-refresh" (click)="reload()"></em>
        </div>
    </div>
    <ng-template #dataSpinner>
        <div class="d-flex justify-content-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </ng-template>
</ng-template>
